import React from 'react';
import { Link } from 'react-router-dom';
import ProductScreenshot from '../products/ProductScreenshot';

type CalloutTheme = 'primary' | 'accent' | 'emerald' | 'violet';

type Callout = {
  benefit: string;
  detail: string;
  theme: CalloutTheme;
  href: string;
  aiIcon?: boolean;
};

const themeStyles: Record<
  CalloutTheme,
  { card: string; iconBg: string; icon: string; detail: string }
> = {
  primary: {
    card: 'border-primary-300/70 dark:border-primary-500/45 shadow-primary-500/15 hover:border-primary-500/90 dark:hover:border-primary-400/60',
    iconBg: 'bg-primary-100 dark:bg-primary-500/20',
    icon: 'text-primary-600 dark:text-primary-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  accent: {
    card: 'border-accent-300/70 dark:border-accent-500/45 shadow-accent-500/15 hover:border-accent-500/90 dark:hover:border-accent-400/60',
    iconBg: 'bg-accent-100 dark:bg-accent-500/20',
    icon: 'text-accent-600 dark:text-accent-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  emerald: {
    card: 'border-emerald-300/70 dark:border-emerald-500/45 shadow-emerald-500/15 hover:border-emerald-500/90 dark:hover:border-emerald-400/60',
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
  violet: {
    card: 'border-violet-300/70 dark:border-violet-500/45 shadow-violet-500/15 hover:border-violet-500/90 dark:hover:border-violet-400/60',
    iconBg: 'bg-violet-100 dark:bg-violet-500/20',
    icon: 'text-violet-600 dark:text-violet-400',
    detail: 'text-slate-600 dark:text-dark-400',
  },
};

const callouts: Callout[] = [
  {
    benefit: 'Save admin time',
    detail: 'Less manual work across orders, jobs, and accounts payable',
    theme: 'primary',
    href: '/products/cimmpleflow',
  },
  {
    benefit: 'Track jobs live',
    detail: 'Real-time visibility on production, jobs, and fulfillment',
    theme: 'emerald',
    href: '/products/cimmpleflow',
  },
  {
    benefit: 'Automate payroll',
    detail: 'Payroll runs, taxes, compliance, and reporting in one place',
    theme: 'accent',
    href: '/products/cimmlepay',
  },
  {
    benefit: 'AI where it counts',
    detail: 'AI extracts invoice data from PDFs & email',
    theme: 'violet',
    href: '/products/cimmpleflow',
    aiIcon: true,
  },
];

const CalloutIcon: React.FC<{ callout: Callout; className: string }> = ({ callout, className }) => {
  if (callout.aiIcon) {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    );
  }
  if (callout.theme === 'emerald') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  }
  if (callout.theme === 'accent') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

const CalloutCard: React.FC<{ callout: Callout }> = ({ callout }) => {
  const theme = themeStyles[callout.theme];

  return (
    <Link
      to={callout.href}
      className={`flex h-full min-h-[132px] w-[248px] sm:w-[256px] lg:w-full flex-col rounded-xl border bg-white/95 dark:bg-dark-900/95 backdrop-blur-sm px-4 py-4 shadow-lg text-left transition-colors duration-300 ${theme.card}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mb-3 ${theme.iconBg}`}>
        <CalloutIcon callout={callout} className={`w-4 h-4 ${theme.icon}`} />
      </div>

      <h3
        className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap mb-1.5"
        title={callout.benefit}
      >
        {callout.benefit}
      </h3>

      <p className={`text-xs leading-relaxed flex-1 ${theme.detail}`}>{callout.detail}</p>
    </Link>
  );
};

const PlatformVisual: React.FC = () => {
  return (
    <div className="mt-16 lg:mt-20 w-full max-w-5xl mx-auto animate-slide-up animate-delay-300">
      <div className="relative">
        <div
          className="absolute -inset-4 sm:-inset-8 rounded-[2rem] bg-gradient-to-br from-primary-500/10 via-transparent to-primary-500/5 blur-2xl pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative">
          <ProductScreenshot
            imageSrc="/images/platform/cimmpleflow-home.png"
            imageAlt="CimmpleFlow dashboard showing orders, jobs, revenue, and operational analytics"
            productName="CimmpleFlow"
            color="primary"
            loading="eager"
          />
          <div
            className="absolute top-0 left-[calc(3.25rem+1in)] sm:left-[calc(3.5rem+1in)] z-20 flex flex-col items-center pointer-events-none"
            aria-hidden="true"
          >
            <div className="relative z-10 -mb-px">
              <div className="w-4 h-4 rounded-full border-2 border-violet-300/90 dark:border-violet-400 bg-white dark:bg-dark-800 shadow-sm" />
              <div className="absolute inset-[5px] rounded-full bg-gradient-to-br from-violet-400/40 to-primary-500/30" />
            </div>

            <div
              className="relative w-[3.75rem] sm:w-16 pt-2.5 pb-5 px-2 text-center shadow-xl shadow-violet-900/25 ring-1 ring-inset ring-white/25"
              style={{
                background: 'linear-gradient(165deg, #8b5cf6 0%, #7c3aed 38%, #4f46e5 100%)',
                clipPath:
                  'polygon(0 0, 100% 0, 100% 88%, 78% 74%, 50% 100%, 22% 74%, 0 88%)',
              }}
            >
              <div
                className="absolute inset-x-1 top-0 h-1/2 rounded-b-sm bg-gradient-to-b from-white/30 to-transparent pointer-events-none"
                aria-hidden="true"
              />
              <div
                className="absolute left-1 top-2 bottom-6 w-px bg-white/20 pointer-events-none"
                aria-hidden="true"
              />
              <p className="relative font-sans">
                <span className="block text-[13px] sm:text-sm font-extrabold text-white leading-none tracking-normal">
                  AI
                </span>
                <span className="block mt-1 text-[10px] sm:text-[11px] font-medium italic lowercase text-violet-100 leading-none tracking-normal">
                  inside
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex flex-row flex-nowrap lg:grid lg:grid-cols-4 gap-3 sm:gap-4 items-stretch justify-start lg:justify-center overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 snap-x snap-mandatory lg:snap-none">
          {callouts.map((callout) => (
            <div key={callout.benefit} className="snap-center flex lg:min-w-0">
              <CalloutCard callout={callout} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformVisual;
